// services/authService.js - Servicio de autenticación
import apiClient from './api';
import { setToken, setRefreshToken, setUser, clearAuth, getRefreshToken, getUser } from '../utils/auth';
import { toast } from 'react-toastify';

class AuthService {
  // Método auxiliar para manejar errores del API
  handleApiError(error, defaultMessage = 'Error en la operación') {
    if (!error.response) {
      // Error de conectividad
      toast.error('Error de conexión. Verifique su conexión a internet.', {
        theme: 'colored'
      });
      return 'Error de conexión';
    }

    const errorData = error.response.data;
    
    // Si hay múltiples errores de validación
    if (errorData.message && Array.isArray(errorData.message)) {
      const errorList = errorData.message.map((msg, index) => `${index + 1}. ${msg}`).join('\n');
      toast.error(
        <div>
          <strong>Errores encontrados:</strong>
          <br />
          <div style={{ whiteSpace: 'pre-line', marginTop: '8px' }}>
            {errorList}
          </div>
        </div>, 
        { 
          theme: 'colored',
          autoClose: 8000 // Más tiempo para leer múltiples errores
        }
      );
      return errorList;
    }

    // Error simple - manejar estructura de respuesta del backend
    let message = defaultMessage;
    if (errorData.error && typeof errorData.error === 'object') {
      message = errorData.error.message || defaultMessage;
    } else if (errorData.error && typeof errorData.error === 'string') {
      message = errorData.error;
    } else if (errorData.message) {
      message = errorData.message;
    }
    
    toast.error(message, { theme: 'colored' });
    return message;
  }

  // Login de usuario
  async login(credentials) {
    try {
      // El interceptor de axios se encargará de agregar X-Tenant-ID basado en subdomain
      console.log('🔐 Login attempt:', {
        username: credentials.username,
        hostname: window.location.hostname,
        // No loguear password por seguridad
      });
      
      const response = await apiClient.post('/auth/login', credentials);
      
      // Manejar estructura de respuesta correcta del backend
      if (response.data.success && response.data.data) {
        const { access_token, refresh_token, user, remember, companyConfig } = response.data.data;
        
        // Guardar tokens y usuario en localStorage o cookies según "recordarme"
        if (remember) {
          // Si el usuario eligió "recordarme", guardamos en cookies con mayor duración
          this.setCookie('access_token', access_token, 30); // 30 días
          this.setCookie('refresh_token', refresh_token, 30); // 30 días
          this.setCookie('user', JSON.stringify(user), 30); // 30 días
          this.setCookie('remember', 'true', 30); // 30 días
          
          // También en localStorage para compatibilidad
          setToken(access_token);
          setRefreshToken(refresh_token);
          setUser(user);
        } else {
          // Sesión normal, solo localStorage (se borra al cerrar el navegador si es sessionStorage)
          setToken(access_token);
          setRefreshToken(refresh_token);
          setUser(user);
          
          // Limpiar cookies por si existían
          this.deleteCookie('access_token');
          this.deleteCookie('refresh_token');
          this.deleteCookie('user');
          this.deleteCookie('remember');
        }
        
        return { success: true, user, remember, companyConfig };
      } else {
        // Respuesta exitosa pero sin datos válidos
        const message = response.data.error || 'Respuesta inválida del servidor';
        return { success: false, error: message };
      }
    } catch (error) {
      // NO usar handleApiError aquí para evitar doble toast
      let message = 'Credenciales incorrectas';
      
      if (!error.response) {
        message = 'Error de conexión. Verifique su conexión a internet.';
      } else if (error.response.data) {
        const errorData = error.response.data;
        if (errorData.error && typeof errorData.error === 'object') {
          message = errorData.error.message || message;
        } else if (errorData.error && typeof errorData.error === 'string') {
          message = errorData.error;
        } else if (errorData.message) {
          message = errorData.message;
        }
      }
      
      // NO mostrar toast aquí - dejar que el formulario maneje el error
      return { success: false, error: message };
    }
  }

  // Métodos auxiliares para cookies
  setCookie(name, value, days) {
    let expires = "";
    if (days) {
      const date = new Date();
      date.setTime(date.getTime() + (days * 24 * 60 * 60 * 1000));
      expires = "; expires=" + date.toUTCString();
    }
    document.cookie = name + "=" + (value || "") + expires + "; path=/; SameSite=Lax";
  }

  getCookie(name) {
    const nameEQ = name + "=";
    const ca = document.cookie.split(';');
    for (let i = 0; i < ca.length; i++) {
      let c = ca[i];
      while (c.charAt(0) === ' ') c = c.substring(1, c.length);
      if (c.indexOf(nameEQ) === 0) return c.substring(nameEQ.length, c.length);
    }
    return null;
  }

  deleteCookie(name) {
    document.cookie = name + "=; Path=/; Expires=Thu, 01 Jan 1970 00:00:01 GMT; SameSite=Lax";
  }

  // Método para cargar desde cookies si existe
  loadFromCookies() {
    const remember = this.getCookie('remember');
    if (remember === 'true') {
      const access_token = this.getCookie('access_token');
      const refresh_token = this.getCookie('refresh_token');
      const userStr = this.getCookie('user');
      
      if (access_token && refresh_token && userStr) {
        try {
          const user = JSON.parse(userStr);
          setToken(access_token);
          setRefreshToken(refresh_token);
          setUser(user);
          return true;
        } catch (error) {
          this.clearCookies();
        }
      }
    }
    return false;
  }

  clearCookies() {
    this.deleteCookie('access_token');
    this.deleteCookie('refresh_token');
    this.deleteCookie('user');
    this.deleteCookie('remember');
  }

  // Registro de usuario con login automático
  async register(userData) {
    try {
      const response = await apiClient.post('/auth/register', userData);
      
      // Después del registro exitoso, hacer login automático
      const loginResult = await this.login({
        username: userData.username,
        password: userData.password
      });

      if (loginResult.success) {
        return {
          success: true,
          user: loginResult.user,
          message: 'Usuario registrado exitosamente'
        };
      } else {
        // Si el registro fue exitoso pero el login falló
        return {
          success: true,
          autoLoginFailed: true,
          data: response.data,
          message: 'Usuario registrado exitosamente. Por favor, inicie sesión.'
        };
      }
    } catch (error) {
      const message = this.handleApiError(error, 'Error al registrar usuario');
      return { success: false, error: message };
    }
  }

  // Logout
  async logout() {
    try {
      await apiClient.post('/auth/logout');
    } catch (error) {
      // Error silencioso - no es crítico
    } finally {
      // Limpiar datos locales independientemente del resultado
      clearAuth();
      // Limpiar cookies también
      this.clearCookies();
    }
  }

  // Refrescar token
  async refreshToken() {
    try {
      const refreshToken = getRefreshToken();
      if (!refreshToken) {
        throw new Error('No refresh token available');
      }

      const response = await apiClient.post('/auth/refresh', {}, {
        headers: {
          'Authorization': `Bearer ${refreshToken}`
        }
      });
      
      // El backend envuelve la respuesta en { success, data }: los tokens vienen en data.
      const body = response.data?.data || response.data;
      const { accessToken, refreshToken: newRefreshToken } = body;

      if (!accessToken) {
        throw new Error('Respuesta de refresh sin accessToken');
      }

      setToken(accessToken);
      if (newRefreshToken) {
        setRefreshToken(newRefreshToken);
      }

      return { success: true, accessToken, refreshToken: newRefreshToken };
    } catch (error) {
      // No borrar la sesión aquí: un fallo transitorio del refresh proactivo no debe
      // cerrar la sesión. El interceptor 401 maneja la expiración real del refresh token.
      console.warn('Refresh de token falló:', error.message);
      return { success: false };
    }
  }

  // Obtener perfil del usuario
  async getProfile() {
    try {
      const response = await apiClient.get('/auth/profile');
      // El backend devuelve {success: true, data: userData, meta: ...}
      const user = response.data.data || response.data;
      setUser(user); // Actualizar datos del usuario
      return { success: true, user };
    } catch (error) {
      const message = this.handleApiError(error, 'Error al obtener perfil');
      return { success: false, error: message };
    }
  }

  // Obtener menús dinámicos para el usuario actual
  async getUserMenus() {
    try {
      const response = await apiClient.get('/auth/user-menus');
      return { success: true, menus: response.data };
    } catch (error) {
      // No mostrar toast para menús, es un error silencioso
      return { success: false, error: 'Error al cargar menús' };
    }
  }

  // Obtener todos los usuarios (solo admin)
  async getUsers() {
    try {
      const response = await apiClient.get('/auth/users');
      return { success: true, users: response.data };
    } catch (error) {
      const message = this.handleApiError(error, 'Error al obtener usuarios');
      return { success: false, error: message };
    }
  }

  // Actualizar usuario
  async updateUser(userId, userData) {
    try {
      const response = await apiClient.patch(`/auth/users/${userId}`, userData);
      toast.success('Usuario actualizado exitosamente', { theme: 'colored' });
      return { success: true, user: response.data };
    } catch (error) {
      const message = this.handleApiError(error, 'Error al actualizar usuario');
      return { success: false, error: message };
    }
  }

  // Actualizar perfil del usuario actual
  async updateProfile(updateData) {
    try {
      const currentUser = getUser();
      if (!currentUser) {
        throw new Error('Usuario no autenticado');
      }

      const response = await apiClient.patch(`/auth/profile`, updateData);
      
      // Actualizar datos del usuario en el contexto
      const updatedUser = response.data.data || response.data;
      setUser(updatedUser);
      
      toast.success('Perfil actualizado exitosamente', { theme: 'colored' });
      return { success: true, user: updatedUser };
    } catch (error) {
      const message = this.handleApiError(error, 'Error al actualizar perfil');
      return { success: false, error: message };
    }
  }

  // Subir foto de perfil
  async uploadProfileImage(userId, formData) {
    try {
      const response = await apiClient.patch(`/auth/${userId}/profile-image`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });
      
      // El backend devuelve { message: "...", data: { profileImageUrl: "...", user: {...} } }
      const { profileImageUrl, user } = response.data.data;
      
      // Actualizar usuario en el contexto si es el usuario actual
      const currentUser = getUser();
      if (currentUser && currentUser.id === userId) {
        // Usar el usuario completo devuelto por el backend
        if (user) {
          setUser(user);
        } else {
          // Fallback: actualizar solo la imagen
          setUser({ ...currentUser, profileImageUrl, updatedAt: new Date().toISOString() });
        }
      }
      
      toast.success('Foto de perfil actualizada exitosamente', { theme: 'colored' });
      return { success: true, data: { profileImageUrl, user } };
    } catch (error) {
      const message = this.handleApiError(error, 'Error al subir la imagen');
      return { success: false, error: message };
    }
  }
}

// Exportar instancia singleton
const authService = new AuthService();
export default authService; 